/**
 * The thread header's decisions. **Pure**, so every case is checkable without a
 * DOM - same split as `panel-view.ts` and `thread-view.ts`.
 *
 * ┌─ ⛔ WHAT THE HEADER HOLDS, DECIDED BEFORE THREE ITEMS LANDED IN IT ─────────┐
 * │ There was no header at all until 2026-08-13, which made this the one        │
 * │ chance to decide its contents rather than watch them accrete. Items 14, 15  │
 * │ and 16 all needed somewhere to live, and the scope document already names   │
 * │ two MORE things for it that are in none of them: the 24-hour window         │
 * │ countdown and presence ("Nina is viewing").                                  │
 * │                                                                            │
 * │   Identity     contact name, channel      whose thread am I in              │
 * │   State        status + snooze            14 and 15 are ONE fact - where    │
 * │                                           this conversation sits - not two  │
 * │   Assignment   holder, or Unassigned      16                                │
 * │   Actions      a single overflow          ⛔ THE ACCRETION POINT             │
 * │                                                                            │
 * │ ⛔ STATE IS THE CONTROL. Clicking the status opens the transition menu;     │
 * │ clicking the assignee opens the picker. Three visible buttons today becomes │
 * │ six when the countdown and presence arrive, above a message list an agent   │
 * │ reads all day. **A new thing gets a slot or it does not belong**, and       │
 * │ nothing is added beside them.                                               │
 * │                                                                            │
 * │ ⚠️ THE RIGHT PANEL SHOWS ALL THREE FACTS TOO, AND THE DUPLICATION IS        │
 * │ DELIBERATE WITH A FALSIFICATION CONDITION: the panel is for reading, the    │
 * │ header for acting, and an agent scrolled down a long thread cannot see the  │
 * │ panel's top. **If that stops holding, the header is what goes.**            │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ AS EVERYWHERE ELSE IN THIS DIRECTORY, THESE RETURN TAGGED UNIONS AND NOT
 * SENTENCES. The component owns the words; this owns which shape applies.
 */
import type { PanelIdentity } from '../../../src/services/conversation-panel.js'
import { personName } from './panel-view.js'

export type Status = 'open' | 'answered' | 'closed'

/**
 * ⛔ EVERY TRANSITION THE CONTROL OFFERS, DERIVED FROM THE CURRENT STATUS
 * RATHER THAN LISTED. A fixed list would offer `open` to an open conversation,
 * and `assignConversation`'s own note says why that matters: re-selecting the
 * current value is a normal thing to do with a mouse, and every one of those is
 * a request that changes nothing.
 *
 * ⚠️ IT IS NOT THE STATE MACHINE. `transitionStatus` accepts any pair and
 * forces open→answered→closed for the close leg; this decides what a person is
 * OFFERED, which is a smaller question. **The server does not trust this list**
 * - the route validates independently - because a control is a convenience and
 * a route is a boundary.
 */
export function transitionsFrom(status: Status): Status[] {
  switch (status) {
    case 'open':
      return ['answered', 'closed']
    case 'answered':
      return ['open', 'closed']
    case 'closed':
      return ['open']
  }
}

/**
 * ⛔ NOTHING CONFIRMS ANY MORE, AND THAT IS A CONSEQUENCE RATHER THAN A CHANGE
 * OF MIND ABOUT CONFIRMS.
 *
 * ┌─ ⚠️ THE REASON WAS REMOVED, SO THE RULE WENT WITH IT. 2026-08-20 ──────────┐
 * │ This function used to return `to === 'closed'`, and its justification was  │
 * │ that **closing was the only transition that wrote a visible artefact into  │
 * │ the customer-facing narrative.** Amir's inspection reversed that: closing  │
 * │ is internal and the customer should see nothing, so `surfaceFor` now maps  │
 * │ `answered → closed` to `activity`.                                        │
 * │                                                                          │
 * │ ⛔ ONCE THE ARTEFACT IS GONE, CLOSE IS AN ORDINARY TRANSITION: reversible  │
 * │ by reopen, invisible to the customer, and no more consequential than       │
 * │ marking answered. A confirm kept past its reason is the thing that trains  │
 * │ people to click through confirms that matter.                              │
 * │                                                                          │
 * │ ⚠️ THE ONE ARGUMENT FOR KEEPING IT IS RECORDED IN `surfaceFor` AND IS      │
 * │ AMIR'S: reopen still writes a thread line, so close has a DELAYED visible  │
 * │ consequence. If reopen becomes `activity` too, that argument goes as well. │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ THE FUNCTION STAYS rather than its call sites being deleted. `Build_Order:483`
 * still keeps close out of the command palette, and the day a transition does
 * become irreversible this is where that decision belongs. It is one line to
 * revive, and its absence would scatter the question across the header.
 */
export function confirmsTransition(_to: Status): boolean {
  return false
}

/**
 * What the confirm has to say, as facts rather than as a sentence.
 *
 * ┌─ ⛔ IT NAMES WHAT WILL HAPPEN AND NEVER ASKS "ARE YOU SURE?" ───────────────┐
 * │ Same argument as the disconnected banner's threshold: a prompt people click │
 * │ through is worse than no prompt, because it also trains them to click       │
 * │ through the next one.                                                       │
 * │                                                                            │
 * │ ⚠️ AND `twoLegs` IS THE PART THAT MUST NOT BE FUDGED. Closing an OPEN       │
 * │ conversation records open→answered→closed, so it produces TWO events, one   │
 * │ of which nobody asked for. The activity feed will show both. A confirm that │
 * │ claimed a single action would be contradicted by the feed a second later,   │
 * │ which is how a prompt teaches people it is not describing reality.          │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
export type CloseConfirm = {
  /** The close leg writes a system line into the thread. Always true. */
  writesThreadLine: true
  /** Closing an `open` conversation also records `answered` on the way. */
  twoLegs: boolean
}

export function closeConfirm(from: Status): CloseConfirm {
  return { writesThreadLine: true, twoLegs: from === 'open' }
}

/**
 * ⛔ UNDO IS NOT OFFERED, AND THIS IS WHERE THE ABSENCE IS RECORDED so it reads
 * as a decision rather than a gap.
 *
 * A transition writes `conversation_events` rows and, on the close leg, a system
 * message. Undoing means either deleting those - falsifying history, the
 * argument that killed hard-delete for notes - or writing a compensating pair,
 * so an accidental close-then-undo leaves FOUR events and TWO system lines in a
 * feed whose whole purpose is being readable.
 *
 * ✅ **REOPENING IS THE RECOVERY**, is already a first-class transition
 * (`closed→open`, which `surfaceFor` calls "reopen is visible"), and produces
 * exactly that honest record.
 */
export const UNDO_IS_NOT_A_TRANSITION = true

/**
 * The state slot: status and snooze as ONE reading, because they are one fact.
 *
 * ⚠️ SNOOZE IS ORTHOGONAL TO STATUS in the schema - a nullable timestamp, not a
 * fourth status - and the header must not flatten them into one word. A snoozed
 * conversation is still open or answered, and an agent needs to know which when
 * it wakes.
 */
export type StateRender = {
  status: Status
  /** ISO instant, or `null` when not snoozed. Formatting belongs to the caller. */
  snoozedUntil: string | null
  snoozeNote: string | null
}

export function stateRender(identity: PanelIdentity): StateRender {
  const note = identity.snoozeNote?.trim()
  return {
    // ⚠️ NOT VALIDATED INTO A FALLBACK. `status` is a CHECK-constrained column;
    // a value outside the three would be a database fault, and rendering it as
    // `open` would hide that behind a plausible word.
    status: identity.status as Status,
    snoozedUntil: identity.snoozeUntil,
    snoozeNote: note === undefined || note === '' ? null : note,
  }
}

/**
 * The assignment slot.
 *
 * ⚠️ IT REUSES `assigneeRender`'s THREE-STATE ANSWER but carries the id as well,
 * because the header's version is a CONTROL and has to mark which option is
 * current. The panel's version only has to render a word.
 */
export type HeaderAssignee =
  | { kind: 'assigned'; userId: string; name: string }
  | { kind: 'unassigned' }

export function headerAssignee(identity: PanelIdentity): HeaderAssignee {
  const name = personName(identity.assignedFirstName, identity.assignedLastName)
  /**
   * ⛔ BOTH HALVES REQUIRED. An `assigned_user_id` with no joined name is a
   * deleted user or a failed join, and rendering "assigned to nobody" would be
   * a third state wearing the second one's clothes. `unassigned` is the honest
   * answer: nobody whose name we can show holds this.
   */
  if (identity.assignedUserId === null || name === null) return { kind: 'unassigned' }
  return { kind: 'assigned', userId: identity.assignedUserId, name }
}

/**
 * The identity slot.
 *
 * ⛔ THE PHONE NUMBER IS THE FALLBACK AND NOT A SECOND LINE. The header is one
 * row above a message list; a contact with no display name is common on
 * WhatsApp and the number IS their name until somebody types one.
 */
export type HeaderIdentity = {
  /**
   * `null` only when we have neither a name nor a number, which the schema
   * permits and the corpus does not contain. ⚠️ THE WORD FOR THAT CASE IS THE
   * COMPONENT'S, not this module's - same rule as everywhere else here.
   */
  title: string | null
  channel: string
}

export function headerIdentity(identity: PanelIdentity): HeaderIdentity {
  const name = identity.displayName?.trim()
  const phone = identity.phoneNumber?.trim()
  const title = name !== undefined && name !== '' ? name : (phone ?? '')
  return { title: title === '' ? null : title, channel: identity.channel }
}
